// src/components/AccountDetailPage/AccountDetailView.jsx
import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import styles from "../../gmanewstyle.module.css";
import logoimage from "../assets/Group -2.png"; // still used for dropdown header, not visible in main view

const AccountDetailView = () => {
  const { accountId } = useParams();
  const navigate = useNavigate();
  const [isDropdownVisible, setDropdownVisible] = useState(false);

  // data state
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");

  // form state
  const [accountName, setAccountName] = useState("");
  const [email, setEmail] = useState("");
  const [mobileNumber, setMobileNumber] = useState("");
  const [registrationId, setRegistrationId] = useState("");
  const [billingAddress1, setBillingAddress1] = useState("");
  const [billingAddress2, setBillingAddress2] = useState("");
  const [billingAddress3, setBillingAddress3] = useState("");

  // access for dropdown
  const [userAccess, setUserAccess] = useState([]);
  const [loadingAccess, setLoadingAccess] = useState(true);

  useEffect(() => {
    document.title = "GMA Portal || Account Detail View";

    const fetchAccountDetail = async () => {
      const token = localStorage.getItem("token");
      if (!token) {
        navigate("/");
        return;
      }

      try {
        const response = await fetch(
          `https://portal.gmacc.co.za/api/account/${accountId}/`,
          {
            method: "GET",
            headers: { Authorization: `Bearer ${token}` },
          }
        );

        if (response.status === 404) {
          setError("Account not found.");
          return;
        }

        const data = await response.json();
        if (response.ok) {
          setAccountName(data.name || "");
          setEmail(data.emailaddress1 || "");
          setMobileNumber(data.telephone1 || "");
          setRegistrationId(data.cj_registrationidpassport || "");
          setBillingAddress1(data.address1_line1 || "");
          setBillingAddress2(data.address1_line2 || "");
          setBillingAddress3(data.address1_line3 || "");
        } else {
          setError(data.error || "Failed to fetch account details.");
        }
      } catch (err) {
        setError("Something went wrong.");
      } finally {
        setLoading(false);
      }
    };

    fetchAccountDetail();
  }, [accountId, navigate]);

  // fetch access for dropdown
  useEffect(() => {
    const fetchUserAccess = async () => {
      const token = localStorage.getItem("token");
      if (!token) {
        navigate("/");
        return;
      }

      try {
        const decodedToken = JSON.parse(atob(token.split(".")[1]));
        const email = decodedToken.email || null;
        const contact_id = decodedToken.contact_id || null;

        if (!email) {
          setLoadingAccess(false);
          return;
        }

        const response = await fetch(
          "https://portal.gmacc.co.za/api/contact/determine-access/",
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify({ email, contact_id }),
          }
        );

        const data = await response.json();
        if (response.ok) {
          setUserAccess(data.access || []);
        }
      } catch (err) {
        // ignore
      } finally {
        setLoadingAccess(false);
      }
    };

    fetchUserAccess();
  }, [navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSuccessMessage("");

    const token = localStorage.getItem("token");
    if (!token) {
      navigate("/");
      return;
    }

    const updatedData = {
      name: accountName,
      emailaddress1: email,
      telephone1: mobileNumber,
      cj_registrationidpassport: registrationId,
      address1_line1: billingAddress1,
      address1_line2: billingAddress2,
      address1_line3: billingAddress3,
    };

    try {
      const response = await fetch(
        `https://portal.gmacc.co.za/api/account/update/${accountId}/`,
        {
          method: "PATCH",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify(updatedData),
        }
      );

      const data = await response.json();
      if (response.ok) {
        setSuccessMessage("✅ Account updated successfully!");
        setError("");
      } else {
        setError(data.error || "❌ Failed to update account.");
        setSuccessMessage("");
      }
    } catch (err) {
      setError("Something went wrong.");
    }
  };

  const [showTicketModal, setShowTicketModal] = useState(false);

  // portal nav from dropdown
  const handleOwnerPortalClick = () => navigate("/owner-portal");
  const handleTrusteePortalClick = () => navigate("/trustee-portal");
  const handleContractorPortalClick = () => navigate("/contractor-portal");

  return (
    <div className={styles.page}>
      {/* MAIN SPLIT LAYOUT (like statement download) */}
      <div className={styles.authFrame}>
        <div className={styles.authMedia} aria-hidden="true"></div>

        <div className={styles.authPanel}>
          {loading ? (
            <div className={styles.page}>
              <div className={styles.shell}>
                <main className={styles.contentArea}>
                  <div className={styles.panel}>
                    <div className={styles.loadingContainer}>
                      <div className={styles.loadingImage}></div>
                      <p className={styles.loadingText}>
                        Loading Account Details...
                      </p>
                    </div>
                  </div>
                </main>
              </div>
            </div>
          ) : (
            <form className={styles.formCard} onSubmit={handleSubmit}>
              <img
                src={logoimage}
                alt="Gauteng Management Agents"
                className={styles.logoimage}
              />
              <h1 className={styles.heading}>ACCOUNT INFORMATION</h1>
              <p className={styles.subhead}>
                You can edit the details of your account here. Changes will
                apply to all linked levy statements.
              </p>

              {error && <p className={styles.errorText}>{error}</p>}
              {successMessage && (
                <p className={styles.successText}>{successMessage}</p>
              )}

              {/* row 1 */}
              <div className={styles.twoCol}>
                <div className={styles.inputShell}>
                  <input
                    type="text"
                    value={accountName}
                    readOnly
                    className={`${styles.input} ${styles.readOnly}`}
                    placeholder="Account Name"
                  />
                </div>
                <div className={styles.inputShell}>
                  <input
                    type="text"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className={styles.input}
                    placeholder="Email"
                    required
                  />
                </div>
              </div>

              {/* row 2 */}
              <div className={styles.twoCol}>
                <div className={styles.inputShell}>
                  <input
                    type="text"
                    value={mobileNumber}
                    onChange={(e) => setMobileNumber(e.target.value)}
                    className={styles.input}
                    placeholder="Mobile Number"
                    required
                  />
                </div>
                <div className={styles.inputShell}>
                  <input
                    type="text"
                    value={registrationId}
                    onChange={(e) => setRegistrationId(e.target.value)}
                    className={styles.input}
                    placeholder="ID / Reg Number"
                    required
                  />
                </div>
              </div>

              {/* row 3 */}
              <div className={styles.inputShell}>
                <input
                  type="text"
                  value={billingAddress1}
                  onChange={(e) => setBillingAddress1(e.target.value)}
                  className={styles.input}
                  placeholder="Billing Address Line 1"
                />
              </div>
              <div className={styles.inputShell}>
                <input
                  type="text"
                  value={billingAddress2}
                  onChange={(e) => setBillingAddress2(e.target.value)}
                  className={styles.input}
                  placeholder="Billing Address Line 2"
                />
              </div>
              <div className={styles.inputShell}>
                <input
                  type="text"
                  value={billingAddress3}
                  onChange={(e) => setBillingAddress3(e.target.value)}
                  className={styles.input}
                  placeholder="Billing Address Line 3"
                />
              </div>

              <button type="submit" className={styles.primaryButton}>
                Update
              </button>

              <div className={styles.backLink} onClick={() => navigate(-1)}>
                Back
              </div>
              <div className={styles.backLink} onClick={() => navigate("/#/")}>
                Log Out
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default AccountDetailView;
